import { filterDeletedAddress, uniqueBy } from '@epicuro-next/utilities';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
  Account,
  AccountCountry,
  AccountReviewer,
  AccountsQuery,
} from 'graphql-api/gql-api-interfaces';

import { ISearchResult } from '../types';

import {
  clearActiveAccount,
  loadAccountCountriesSuccess,
  loadAccountDetailsSuccess,
  loadAccountReviewers,
  loadAccountReviewersFailure,
  loadAccountReviewersSuccess,
  loadAccounts,
  loadAccountsFailure,
  loadAccountsSuccess,
  resetAccountsSearchHistory,
  resetPaginationQuery,
  setAccountBESearchEnabled,
  setActiveAccount,
  setPaginationQuery,
  setReviewerSearchQuery,
  setSearchQuery,
  upsertAccounts,
} from './actions/accounts.actions';

export const PAGE_SIZE = 40;
export const ONCE_GET_PAGE_SIZE = 1000;
export const FIRST_PAGE = 1;

export const accountsFeatureKey = 'accounts';

export interface State extends EntityState<Account> {
  /** Active account to use in app and show data in user menu */
  activeAccount: Account | null;

  /** Accounts loading flag */
  loading: boolean;

  /** Accounts loaded flag */
  loaded: boolean;

  /** Flag to determine if all account without search are loaded */
  /** impossible to use total and entity count as all search results, and basket accounts are also in entity */
  allAccountPagesLoaded: boolean;

  /** Accounts response error */
  error: string | null;

  /** Accounts request query */
  query: AccountsQuery;

  pageWithoutSearch: number;

  /** Accounts current search result */
  searchResult: ISearchResult;

  /** All accounts search result ids */
  searchHistoryIds: string[];

  availableCountries: AccountCountry[];

  reviewers: AccountReviewer[];

  reviewersLoading: boolean;

  reviewersSearchQuery: string;

  /** Enable/Disable BE search by User Role */
  isBESearchEnabled?: boolean;
}

export const adapter: EntityAdapter<Account> = createEntityAdapter<Account>({
  selectId: (account: Account) => account.accountId,
  sortComparer: false,
});

export const initialState: State = adapter.getInitialState({
  activeAccount: null,
  loaded: false,
  loading: false,
  allAccountPagesLoaded: false,
  error: null,
  pageWithoutSearch: FIRST_PAGE,
  query: {
    page: FIRST_PAGE,
    size: PAGE_SIZE,
    query: '',
  },
  searchResult: {
    allSearchAccountPagesLoaded: false,
    list: [],
    loaded: false,
  },
  searchHistoryIds: [],
  availableCountries: [],
  reviewers: [],
  reviewersLoading: false,
  reviewersSearchQuery: '',
});

export const reducer = createReducer(
  initialState,

  on(loadAccounts, (state): State => ({ ...state, loading: true })),

  on(setAccountBESearchEnabled, (state, { isBESearchEnabled }) => ({
    ...state,
    isBESearchEnabled,
  })),

  on(loadAccountsSuccess, (state, { list, pageSize }): State => {
    const isSearchResponse = state.query.query;
    const allLoaded = list.length < (pageSize ?? PAGE_SIZE);
    const allAccountPagesLoaded = isSearchResponse
      ? state.allAccountPagesLoaded
      : allLoaded;

    const allSearchAccountPagesLoaded = isSearchResponse
      ? allLoaded
      : state.searchResult.allSearchAccountPagesLoaded;

    const filteredAccounts = list.filter(
      (account) => account.shipToAddresses.length,
    );

    const updated = {
      ...state,
      loaded: true,
      loading: false,
      error: null,
      allAccountPagesLoaded,
      searchResult: isSearchResponse
        ? {
            allSearchAccountPagesLoaded,
            list: uniqueBy(
              [...state.searchResult.list, ...filteredAccounts],
              'accountId',
              'externalId',
            ),
            loaded: true,
          }
        : state.searchResult,
    };

    if (isSearchResponse) {
      updated.searchHistoryIds = state.searchHistoryIds.concat(
        list
          .map((account) => account.accountId)
          .filter((accountId) => !updated.entities[accountId]),
      );
    }

    // add search and pagination result to entity also
    return adapter.upsertMany(filteredAccounts, {
      ...updated,
    });
  }),

  on(
    loadAccountsFailure,
    (state, { error }): State => ({
      ...state,
      error,
      loading: false,
    }),
  ),

  on(setSearchQuery, (state, { query }): State => {
    return {
      ...state,
      query: {
        query,
        size: PAGE_SIZE,
        page: FIRST_PAGE,
      },
      searchResult: {
        list: [],
        allSearchAccountPagesLoaded: false,
        loaded: false,
      },
    };
  }),

  on(setPaginationQuery, (state): State => {
    const query = { ...state.query };

    if (query.query) {
      query.page = (query.page as number) + 1;
      return {
        ...state,
        query,
      };
    }

    return {
      ...state,
      pageWithoutSearch: state.pageWithoutSearch + 1,
    };
  }),

  on(clearActiveAccount, (state) => ({
    ...state,
    activeAccount: null,
  })),

  on(
    setActiveAccount,
    loadAccountDetailsSuccess,
    (state, { account }): State => {
      return adapter.upsertOne(account, {
        ...state,
        activeAccount: account,
      });
    },
  ),

  on(upsertAccounts, (state, { accounts }): State => {
    const filteredAccounts = filterDeletedAddress(accounts);

    return adapter.upsertMany(filteredAccounts, {
      ...state,
    });
  }),

  on(resetAccountsSearchHistory, (state, { skipIds }) => {
    const idsToRemove = state.searchHistoryIds.filter(
      (id) => !skipIds.includes(id),
    );

    return adapter.removeMany(idsToRemove, {
      ...state,
      searchHistoryIds: [],
      searchResult: {
        total: 0,
        allSearchAccountPagesLoaded: false,
        list: [],
        loaded: false,
      },
    });
  }),

  on(resetPaginationQuery, (state) => ({
    ...state,
    pageWithoutSearch: FIRST_PAGE,
    query: {
      page: FIRST_PAGE,
      size: PAGE_SIZE,
      query: '',
    },
  })),
  on(loadAccountCountriesSuccess, (state, { countries }) => ({
    ...state,
    availableCountries: countries,
  })),
  on(loadAccountReviewers, (state) => ({
    ...state,
    reviewersLoading: true,
    reviewersSearchQuery: '',
  })),
  on(loadAccountReviewersSuccess, (state, { reviewers }) => ({
    ...state,
    reviewers,
    reviewersLoading: false,
  })),
  on(loadAccountReviewersFailure, (state) => ({
    ...state,
    reviewersLoading: false,
  })),
  on(setReviewerSearchQuery, (state, { query }) => ({
    ...state,
    reviewersSearchQuery: query,
  })),
);
